'use client';
import { useMutation } from '@tanstack/react-query';
import Axios from '@/lib/axiosConfig';
import { useAuth } from '@/context/Auth';
import { useQuery } from '@tanstack/react-query';

export interface Member {
  id: string;
  firstName: string;
  lastName: string;
  fullName:string;
  profilePictureURL?: string;
  user: {
    username: string;
    email: string;
  };
}

interface SearchParams {
  query: string;
  offset?: number;
  limit?: number;
}

// `/profiles/search` takes `searchText` plus pagination and returns the same
// { rows, count } envelope as the old `/profiles` listing. Rows may or may not
// carry the nested `user` object, so read it defensively — the share/chat UI
// only needs the id, name and avatar.
// `/profiles/search` returns only `fullName`, while the share/chat UI builds
// initials and labels from first/last. Derive them so those call sites always
// get strings instead of undefined.
const splitFullName = (fullName?: string) => {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
  return {
    first: parts[0] ?? '',
    last: parts.length > 1 ? parts[parts.length - 1] : '',
  };
};

const toMember = (row: any): Member => {
  const { first, last } = splitFullName(row.fullName);
  return {
    // Same convention the members page uses: prefer the user id, fall back to
    // the profile id. Everything downstream (DMs, sharing) needs the user id.
    id: row.userID || row.id,
    firstName: row.firstName ?? first,
    lastName: row.lastName ?? last,
    fullName: row.fullName ?? [first, last].filter(Boolean).join(' '),
    profilePictureURL: row.profilePictureURL ?? undefined,
    user: {
      username: row.user?.username ?? '',
      email: row.user?.email ?? '',
    },
  };
};

export const useSearchMembers = () => {
  const { getAPIRequestTokenHeaders } = useAuth();
  return useMutation({
    mutationFn: async ({ query, offset = 0, limit = 20 }: SearchParams) => {
      const response = await Axios.get('/profiles/search', {
        params: {
          searchText: query,
          offset,
          limit
        },
        headers: getAPIRequestTokenHeaders(),
      });
      return (response.data.responseObject?.rows ?? []).map(toMember) as Member[];
    },
  });
};

export const useFetchRecentShared = (limit:number) => {
  const { getAPIRequestTokenHeaders } = useAuth();
  return useQuery({
    queryKey: ['recent-shared', limit],
    queryFn: async () => {
      // No searchText — the endpoint treats it as optional and returns the
      // first page of members, which is what this list wants.
      const response = await Axios.get('/profiles/search', {
        params: { limit, offset: 0 },
        headers: getAPIRequestTokenHeaders(),
      });
      return (response.data.responseObject?.rows ?? []).map(toMember) as Member[];
    },
    enabled: false, //controll when the component will called
  });
};
